import {
	useEffect,
	useLayoutEffect,
	useRef,
	type CSSProperties,
	type HTMLAttributes,
	type ReactNode,
	type RefObject,
} from "react";
import { cn } from "@/lib/cn";

export type PopoverAlign = "start" | "end";

type PopoverProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	anchorRef: RefObject<HTMLElement | null>;
	children: ReactNode;
	className?: string;
	align?: PopoverAlign;
	offset?: number;
	/** Re-run placement when the anchor identity changes while open. */
	placementKey?: string | number | null;
	role?: HTMLAttributes<HTMLDivElement>["role"];
	"aria-label"?: string;
};

export function Popover({
	open,
	onOpenChange,
	anchorRef,
	children,
	className,
	align = "end",
	offset = 6,
	placementKey,
	role,
	"aria-label": ariaLabel,
}: PopoverProps) {
	const ref = useRef<HTMLDivElement>(null);

	const place = () => {
		const el = ref.current;
		const anchor = anchorRef.current;
		if (!el || !anchor) return false;

		const r = anchor.getBoundingClientRect();
		const width = el.offsetWidth || 128;
		const height = el.offsetHeight || 40;
		let top = r.bottom + offset;
		if (top + height > window.innerHeight - 8) {
			top = Math.max(8, r.top - height - offset);
		}
		const left =
			align === "end"
				? Math.min(r.right - width, window.innerWidth - width - 8)
				: Math.max(8, r.left);
		el.style.top = `${top}px`;
		el.style.left = `${Math.max(8, left)}px`;
		return true;
	};

	useLayoutEffect(() => {
		if (!open) return;

		place();

		let tries = 0;
		let raf = 0;
		const retry = () => {
			if (place() || tries >= 8) return;
			tries += 1;
			raf = requestAnimationFrame(retry);
		};
		if (!anchorRef.current) {
			raf = requestAnimationFrame(retry);
		}

		const onScrollOrResize = () => {
			place();
		};
		window.addEventListener("scroll", onScrollOrResize, true);
		window.addEventListener("resize", onScrollOrResize);
		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener("scroll", onScrollOrResize, true);
			window.removeEventListener("resize", onScrollOrResize);
		};
	}, [open, anchorRef, align, offset, placementKey, children]);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const onToggle = (e: Event) => {
			const te = e as ToggleEvent;
			if (te.newState === "closed") onOpenChange(false);
		};

		el.addEventListener("toggle", onToggle);

		if (open) {
			if (!el.matches(":popover-open")) el.showPopover();
			place();
			requestAnimationFrame(() => place());
		} else if (el.matches(":popover-open")) {
			el.hidePopover();
		}

		return () => {
			el.removeEventListener("toggle", onToggle);
		};
	}, [open, onOpenChange, placementKey]);

	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onOpenChange(false);
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, onOpenChange]);

	const style: CSSProperties = {
		position: "fixed",
		margin: 0,
		inset: "auto",
		top: 0,
		left: 0,
	};

	return (
		<div
			ref={ref}
			popover="auto"
			role={role}
			aria-label={ariaLabel}
			className={cn(
				"z-[80] overflow-hidden rounded-[var(--radius-panel)] bg-surface",
				"shadow-[var(--shadow-pop)] ring-1 ring-[var(--ring-hairline)]",
				"m-0 border-0 p-0 text-fg",
				className,
			)}
			style={style}
		>
			{children}
		</div>
	);
}
